'use client';

import { useCallback, useMemo, useState } from 'react';
import { Ruler } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Label } from '@kit/ui/label';
import { cn } from '@kit/ui/utils';

import {
  conversions,
  HeightImperialInput,
  MeasurementDisplay,
  MeasurementInputField,
  UnitToggleButton,
} from './measurement';
import { MEASUREMENT_BOUNDS } from './measurement/measurement-constants';
import { useUnitPreference } from './measurement/use-unit-preference';
import type { MeasurementInputProps } from './types';

/**
 * Comprehensive measurement input component with optimized performance
 * Handles metric/imperial conversions and user preferences
 */
export function MeasurementInput({
  label,
  name: _name,
  value,
  placeholder,
  measurementType,
  defaultUnit = 'metric',
  onChange,
  error,
  required = false,
  min,
  max,
  className,
}: MeasurementInputProps) {
  const { t } = useTranslation();
  const [unit, setUnit, isLoadingPreference] = useUnitPreference(
    measurementType,
    defaultUnit,
  );
  const [localError, setLocalError] = useState<string | undefined>();

  // Get bounds for this measurement type
  const bounds = useMemo(() => {
    const typeBounds = MEASUREMENT_BOUNDS[measurementType];
    return {
      min: min ?? typeBounds?.min,
      max: max ?? typeBounds?.max,
    };
  }, [measurementType, min, max]);

  // Immediate validation function (no debounce for better UX)
  const validateValue = useCallback(
    (rawValue: string, metricValue: number) => {
      // Only validate if value seems complete
      if (!rawValue || rawValue.length < 2) {
        setLocalError(undefined);
        return;
      }

      // Validate bounds
      if (bounds.min !== undefined && metricValue < bounds.min) {
        const displayMin =
          unit === 'imperial'
            ? measurementType === 'weight'
              ? conversions.kgToLbs(bounds.min)
              : conversions.cmToInches(bounds.min)
            : bounds.min;
        setLocalError(
          t('dancer:measurement.valueAtLeast', { value: displayMin }),
        );
        return;
      }

      if (bounds.max !== undefined && metricValue > bounds.max) {
        const displayMax =
          unit === 'imperial'
            ? measurementType === 'weight'
              ? conversions.kgToLbs(bounds.max)
              : conversions.cmToInches(bounds.max)
            : bounds.max;
        setLocalError(
          t('dancer:measurement.valueNotExceed', { value: displayMax }),
        );
        return;
      }

      // Clear error if validation passes
      setLocalError(undefined);
    },
    [bounds, unit, measurementType, setLocalError, t],
  );

  // Memoize display values to prevent unnecessary recalculations
  const displayValues = useMemo(() => {
    if (!value || value === 0) {
      return {
        displayValue: '',
        feetValue: '',
        inchesValue: '',
      };
    }

    if (unit === 'imperial' && measurementType === 'height') {
      const { feet, inches } = conversions.cmToFeet(value);
      return {
        displayValue: '',
        feetValue: feet.toString(),
        inchesValue: inches.toString(),
      };
    }

    let displayValue = value.toString();
    if (unit === 'imperial') {
      if (measurementType === 'weight') {
        displayValue = conversions.kgToLbs(value).toString();
      } else if (measurementType !== 'height') {
        displayValue = conversions.cmToInches(value).toString();
      }
    }

    return {
      displayValue,
      feetValue: '',
      inchesValue: '',
    };
  }, [value, unit, measurementType]);

  const handleUnitToggle = useCallback(() => {
    const newUnit = unit === 'metric' ? 'imperial' : 'metric';
    setUnit(newUnit);
  }, [unit, setUnit]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (!newValue) {
        onChange(0);
        setLocalError(undefined);
        return;
      }

      const numValue = parseFloat(newValue);
      if (isNaN(numValue)) {
        // For invalid numbers, still allow typing but don't update form value
        return;
      }

      // Convert to metric (database standard)
      let metricValue = numValue;
      if (unit === 'imperial') {
        if (measurementType === 'weight') {
          metricValue = conversions.lbsToKg(numValue);
        } else if (measurementType !== 'height') {
          metricValue = conversions.inchesToCm(numValue);
        }
      }

      // Always update the form value immediately
      onChange(metricValue);

      // Trigger immediate validation for better UX
      validateValue(newValue, metricValue);
    },
    [unit, measurementType, onChange, validateValue],
  );

  const handleHeightChange = useCallback(
    (feet: string, inches: string) => {
      // Clear any previous errors
      setLocalError(undefined);

      const feetNum = parseInt(feet) || 0;
      const inchesNum = parseFloat(inches) || 0;

      if (feetNum === 0 && inchesNum === 0) {
        onChange(0);
        return;
      }

      // Validate inches range
      if (inchesNum >= 12) {
        setLocalError(t('dancer:measurement.inchesLessThan12'));
        return;
      }

      const metricValue = conversions.feetToCm(feetNum, inchesNum);

      // Validate bounds
      if (bounds.min !== undefined && metricValue < bounds.min) {
        setLocalError(t('dancer:measurement.heightBelowMinimum'));
        return;
      }

      if (bounds.max !== undefined && metricValue > bounds.max) {
        setLocalError(t('dancer:measurement.heightExceedsMaximum'));
        return;
      }

      onChange(metricValue);
    },
    [onChange, bounds, t],
  );

  const isHeightImperial = unit === 'imperial' && measurementType === 'height';

  // Don't render until preferences are loaded to prevent flashing
  if (isLoadingPreference) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Ruler className="text-muted-foreground h-4 w-4" />
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>

        <UnitToggleButton
          unit={unit}
          measurementType={measurementType}
          onToggle={handleUnitToggle}
        />
      </div>

      <div>
        {isHeightImperial ? (
          <HeightImperialInput
            feetValue={displayValues.feetValue}
            inchesValue={displayValues.inchesValue}
            onChange={handleHeightChange}
            label={label}
            min={bounds.min}
            max={bounds.max}
          />
        ) : (
          <MeasurementInputField
            value={displayValues.displayValue}
            onChange={handleValueChange}
            placeholder={placeholder}
            label={label}
            unit={unit}
            measurementType={measurementType}
            min={
              unit === 'imperial' && measurementType === 'weight' && bounds.min
                ? conversions.kgToLbs(bounds.min)
                : bounds.min
            }
            max={
              unit === 'imperial' && measurementType === 'weight' && bounds.max
                ? conversions.kgToLbs(bounds.max)
                : bounds.max
            }
          />
        )}
      </div>

      <MeasurementDisplay
        value={value || 0}
        measurementType={measurementType}
        currentUnit={unit}
      />

      {(localError || error) && (
        <p className="text-xs text-red-500">{localError || error}</p>
      )}
    </div>
  );
}
